"use client";

import { useLayoutEffect, useRef, useState } from "react";

import { resolveArticlePlaceholder } from "@/data/articleThumbnailPlaceholder";

interface CustomImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  phImg?: string;
  /** Overrides responsive placeholder; omit to use defaults when `phImg` is unset or a legacy grey path. */
  phImgSrcSet?: string;
}

const CustomImage = ({
  src,
  phImg,
  phImgSrcSet,
  loading = "lazy",
  srcSet: contentSrcSet,
  sizes,
  alt,
  className,
  style,
  ...imgProps
}: CustomImageProps) => {
  const { src: resolvedPhImg, srcSet: defaultPhSrcSet } = resolveArticlePlaceholder(phImg);
  const resolvedPhSrcSet = phImgSrcSet !== undefined ? phImgSrcSet || undefined : defaultPhSrcSet;

  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const showPlaceholder = !src || hasError;
  const displaySrc = showPlaceholder ? resolvedPhImg : src;
  const displaySrcSet = showPlaceholder ? resolvedPhSrcSet : contentSrcSet;
  const altText = alt && alt.length > 0 ? alt : "";
  const imageClassName = ["absolute inset-0 h-full w-full", className].filter(Boolean).join(" ");

  // Reset error state when src changes without a loading opacity flash.
  useLayoutEffect(() => {
    setHasError(false);
  }, [src]);

  return (
    <span className="relative block h-full w-full overflow-hidden bg-[#f2f2f2]">
      <img
        ref={imgRef}
        {...imgProps}
        className={imageClassName}
        sizes={sizes}
        src={displaySrc}
        srcSet={displaySrcSet}
        onError={() => setHasError(true)}
        alt={altText}
        loading={loading}
        style={style}
      />
    </span>
  );
};

export default CustomImage;
